import Icon from "~/components/ui/Icon";
import Slider from "~/components/ui/Slider";
import SliderJS from "~/components/ui/SliderJS";
import { useEffect, useId } from "react";
import { useSignal } from "~/sdk/signal";
import type { ImageWidget } from "~/types/widgets";
import { Picture } from "~/components/ui/Picture";

/**
 * Deriva a thumbnail automática do Cloudflare Stream quando o banner não
 * define `videoPoster` explicitamente — sem isso, o <video> fica sem poster
 * e mostra um vão em branco/cinza até os primeiros frames carregarem
 * (mediaType: "video" com preload="metadata" não baixa frame nenhum de
 * antemão). Cloudflare Stream expõe essa thumbnail em toda conta, sempre no
 * mesmo caminho `/thumbnails/thumbnail.jpg`.
 */
function cloudflareStreamThumbnail(videoUrl?: string): string | undefined {
  if (!videoUrl) return undefined;
  const match = videoUrl.match(
    /^(https:\/\/customer-[^/]+\.cloudflarestream\.com\/[^/]+)\//,
  );
  return match ? `${match[1]}/thumbnails/thumbnail.jpg` : undefined;
}

/** Valida se o banner tem mídia válida */
function isValidBanner(banner: Banner): boolean {
  if (banner.mediaType === "video") {
    return !!(banner.desktopVideo || banner.mobileVideo);
  } else {
    return !!(banner.desktop || banner.mobile);
  }
}

/** @titleBy alt */
export interface Banner {
  desktop?: ImageWidget;
  mobile?: ImageWidget;
  alt: string;
  href: string;
  mediaType: "image" | "video";
  desktopVideo?: string;
  mobileVideo?: string;
  videoPoster?: ImageWidget;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
}

export interface Props {
  images?: Banner[];
  preload?: boolean;
  showArrows?: boolean;
  interval?: number;
  maxHeight?: string;
  /**
   * @title Proporção das peças de desktop (largura ÷ altura)
   * @description Padrão 1.7778 (16:9). A altura do banner é calculada por esta
   * proporção, para a peça aparecer inteira, sem faixa branca e sem corte.
   */
  desktopAspectRatio?: number;
  /**
   * @title Proporção das peças de mobile (largura ÷ altura)
   * @description Padrão 0.75 (3:4).
   */
  mobileAspectRatio?: number;
}

const DEFAULT_PROPS = {
  images: [
    {
      alt: "Banner Feminino",
      href: "https://www.deco.cx/",
      mobile:
        "https://decoims.com/danielatombini/67283b41-a57d-44ad-ba48-c29aa42e8963/24278f9e_24278f9e-412d-4a8a-b2d3-57353bb1b368.png",
      desktop:
        "https://decoims.com/danielatombini/cb479892-0653-42bd-96cc-4261f736fee2/afa2c07c_afa2c07c-74f4-496d-8647-5cc58f48117b.png",
      mediaType: "image" as const,
    },
    {
      alt: "Banner Promocional",
      href: "https://www.deco.cx/",
      mobile:
        "https://decoims.com/danielatombini/edc55fa9-f13c-4ff3-b25b-6fa9fe7134a3/eeaa624c_eeaa624c-a3e1-45e8-a6fe-034233cfbcd0.png",
      desktop:
        "https://decoims.com/danielatombini/99ee47f1-8522-4058-93b5-bef6f09c9c31/7949d031_7949d031-9a79-4639-b85e-62fd90af85a9.webp",
      mediaType: "image" as const,
    },
  ],
  preload: true,
};

function BannerVideoItem({
  image,
  lcp,
  onLoad,
}: {
  image: Banner;
  lcp?: boolean;
  onLoad?: () => void;
}) {
  const {
    alt,
    href,
    desktopVideo,
    mobileVideo,
    videoPoster,
    autoplay = true,
    muted = true,
    loop = true,
  } = image;

  // Define fallbacks: desktop usa mobile como fallback e vice-versa
  const finalDesktopVideo = desktopVideo || mobileVideo;
  const finalMobileVideo = mobileVideo || desktopVideo;
  const finalPoster = videoPoster ||
    cloudflareStreamThumbnail(finalDesktopVideo) ||
    cloudflareStreamThumbnail(finalMobileVideo);

  if (!finalDesktopVideo && !finalMobileVideo) {
    return (
      <div className="relative w-full bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">Vídeo não disponível</p>
      </div>
    );
  }

  // Um único <video> com duas <source media="...">, em vez de dois <video>
  // separados escondidos por CSS (`hidden lg:block`/`block lg:hidden`):
  // `display:none` não impede o navegador de baixar um <video autoPlay>, só
  // esconde visualmente — resultava nas DUAS variantes (mobile e desktop)
  // sendo baixadas por completo em toda visita, dobrando o peso de vídeo da
  // home à toa. `<source media>` é resolvido nativamente pelo navegador
  // antes de iniciar o download, baixando só a fonte que casa com o
  // viewport (mesmo mecanismo do `<picture><source media>`).
  return (
    <a
      href={href ?? "#"}
      aria-label={alt}
      className="relative block w-full max-w-full h-full overflow-hidden"
    >
      <video
        // `object-cover` (não `contain`): o slot do banner tem altura fixa
        // (100svh por padrão) e largura 100vw, então sua proporção varia com
        // a tela e quase nunca bate com a do arquivo (16:9). Com `contain` o
        // vídeo era "encaixado" inteiro dentro do slot e sobravam barras
        // vazias (brancas) nas laterais em telas largas/baixas — exatamente
        // o problema relatado. `cover` preenche o slot em qualquer
        // proporção, sem nunca deixar vão; o excedente é aparado pelo
        // `overflow-hidden` do container, mesmo comportamento esperado de um
        // banner full-bleed.
        // `object-bottom` complementa o `cover`: quando a janela é mais
        // "larga e baixa" que 16:9, o corte do cover acontece no eixo
        // VERTICAL — e, centralizado (padrão), ele come justamente a faixa
        // de baixo do vídeo, onde mora o CTA ("SHOP NOW"). Era isso que
        // jogava o CTA pra cima dos dots (e, em telas ultrawide, pra fora
        // da área visível). Ancorando embaixo, o rodapé do vídeo nunca é
        // cortado: o CTA sobe e fica sempre folgado acima dos dots. Em
        // telas mais altas que 16:9 (mobile/tablet) o corte é horizontal e
        // essa âncora não tem efeito nenhum.
        className="object-cover object-bottom w-full max-w-full h-full max-h-full"
        // Proporção declarada do arquivo (16:9), usada só como referência
        // de layout antes dos metadados carregarem — o enquadramento final
        // é do `object-cover` acima.
        width={1920}
        height={1080}
        autoPlay={autoplay}
        muted={muted}
        loop={loop}
        playsInline
        poster={finalPoster}
        preload={lcp ? "auto" : "metadata"}
        onLoadedData={onLoad}
      >
        {finalDesktopVideo && (
          <source media="(min-width: 1024px)" src={finalDesktopVideo} type="video/mp4" />
        )}
        {finalMobileVideo && <source src={finalMobileVideo} type="video/mp4" />}
        Seu navegador não suporta vídeos HTML5.
      </video>
    </a>
  );
}

function BannerItem({
  image,
  lcp,
  onLoad,
}: {
  image: Banner;
  lcp?: boolean;
  onLoad?: () => void;
}) {
  const { alt, mobile, desktop, href, mediaType } = image;

  if (mediaType === "video") {
    return <BannerVideoItem image={image} lcp={lcp} onLoad={onLoad} />;
  }

  if (!desktop && !mobile) {
    return (
      <div className="relative w-full bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">Imagem não disponível</p>
      </div>
    );
  }

  const finalDesktop = desktop || mobile;
  const finalMobile = mobile || desktop;

  if (!finalDesktop || !finalMobile) {
    return (
      <div className="relative w-full bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">Imagem não disponível</p>
      </div>
    );
  }

  return (
    <a
      href={href ?? "#"}
      aria-label={alt}
      className="relative block w-full max-w-full h-full overflow-hidden"
    >
      <Picture
        preload={lcp}
        // Sem isto o `h-full` do <img> abaixo não resolve: ele herda a
        // altura AUTOMÁTICA do <picture> (inline, sem altura própria) e a
        // imagem acaba com a altura natural da sua proporção dentro de uma
        // caixa de altura fixa — sobrava faixa vazia embaixo em telas
        // "largas e baixas" e a imagem transbordava em telas estreitas e
        // altas. Com o <picture> em block/h-full, o <img> preenche a caixa
        // inteira e o `object-cover` abaixo passa a valer de fato.
        pictureClassName="block w-full max-w-full h-full max-h-full"
        sources={[
          {
            media: "(max-width: 1023px)",
            // Sem `height` fixa no resizer: pedimos só a `width` pra Deco
            // preservar a proporção real do arquivo original ao gerar o
            // srcset (evita mandar um crop já deformado pro navegador).
            // O enquadramento final (contido, sem cortar) é feito pelo
            // `object-contain` abaixo, dentro da caixa de altura fixa do
            // container — não pelo resizer.
            src: finalMobile,
            width: 750,
            // O carousel é w-full em qualquer breakpoint — sem isso, o
            // `sizes` default (`${width}px` = "750px") mente sobre o
            // tamanho real do elemento, forçando o navegador a sempre
            // escolher a variante 2x mesmo quando a real (w-full em
            // telas estreitas) não precisaria dela.
            sizes: "100vw",
          },
          {
            media: "(min-width: 1024px)",
            src: finalDesktop,
            width: 1920,
            height: 1080,
            sizes: "100vw",
          },
        ]}
        // `object-cover` (antes `object-contain`), mesmo tratamento do
        // <video> acima e pelo mesmo motivo: a caixa do banner tem
        // altura FIXA (100svh) e largura 100vw, então sua proporção varia
        // com a tela e quase nunca bate com a do arquivo. Com o
        // `object-contain` que havia aqui, a imagem era "encaixada" inteira
        // dentro da caixa e sobravam as barras brancas nas laterais em
        // telas largas/baixas — o mesmo defeito relatado no vídeo da home,
        // que aparece também nos carrosséis de /vivame e /tombini (que são
        // só de imagem e usam este mesmo componente). `cover` preenche a
        // caixa em qualquer proporção e o excedente é aparado pelo
        // `overflow-hidden` do container.
        // Aqui o corte fica CENTRALIZADO (padrão), diferente do
        // `object-bottom` do <video>: a peça do vídeo tem o CTA gravado
        // lá embaixo, enquanto os banners de imagem trazem título/CTA por
        // volta da metade da altura — ancorar embaixo empurraria essa
        // faixa pra cima, pra debaixo do header. Centralizado, o corte é
        // dividido entre topo e rodapé e a arte fica onde a peça previa.
        // `h-full max-h-full` faz a imagem respeitar o limite de altura do
        // container em vez de crescer conforme sua proporção natural
        // (isso, +overflow-hidden no container, é o que elimina o
        // scroll/overflow interno relatado).
        className="object-cover w-full max-w-full h-full max-h-full"
        loading={lcp ? "eager" : "lazy"}
        fetchPriority={lcp ? "high" : "low"}
        decoding="async"
        src={finalMobile}
        alt={alt}
        width={750}
        onLoad={onLoad}
      />
    </a>
  );
}

function Dots({ images, interval = 0 }: Props) {
  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @property --dot-progress {
            syntax: '<percentage>';
            inherits: false;
            initial-value: 0%;
          }
        `,
        }}
      />
      <ul className="carousel justify-center col-span-full gap-6 z-10 row-start-4">
        {images?.map((_, index) => (
          <li className="carousel-item">
            <Slider.Dot index={index}>
              <div className="border-solid rounded-full border-white border-2">
                <div
                  className="w-2.5 h-2.5 rounded-full group-disabled:animate-progress bg-gradient-to-r from-base-100 from-[length:var(--dot-progress)] to-[rgba(255,255,255,0.4)] to-[length:var(--dot-progress)]"
                  style={{ animationDuration: `${interval}s` }}
                />
              </div>
            </Slider.Dot>
          </li>
        ))}
      </ul>
    </>
  );
}

function Buttons() {
  return (
    <>
      <div className="hidden lg:flex items-center justify-center z-10 col-start-1 row-start-2">
        <Slider.PrevButton
          className="flex items-center justify-center rounded-full bg-[#FFFFFF] h-10 w-10 group border-[1px] border-transparent opacity-[0.32] hover:opacity-[1] transition-all duration-300"
          style={{ boxShadow: "0px 0px 16px 0px #1A1A1A1F" }}
        >
          <Icon
            className="text-[#7A7A7A] group-hover:text-[#1A1A1A]"
            size={22}
            id="ChevronLeft"
            strokeWidth={2.5}
          />
        </Slider.PrevButton>
      </div>
      <div className="hidden lg:flex items-center justify-center z-10 col-start-3 row-start-2">
        <Slider.NextButton
          className="flex items-center justify-center rounded-full bg-[#FFFFFF] h-10 w-10 group border-[1px] border-transparent opacity-[0.32] hover:opacity-[1] transition-all duration-300"
          style={{ boxShadow: "0px 0px 16px 0px #1A1A1A1F" }}
        >
          <Icon
            className="text-[#7A7A7A] group-hover:text-[#1A1A1A]"
            size={24}
            id="ChevronRight"
            strokeWidth={2}
          />
        </Slider.NextButton>
      </div>
    </>
  );
}

/**
 * BUG: "scroll não desce dependendo de onde está o cursor" (banner full da
 * home). Causa raiz: `.carousel` (src/styles/app.css) roda
 * `overflow-x: auto; scroll-snap-type: x mandatory;` pra permitir o
 * swipe/arraste horizontal entre slides — e esse elemento aqui ocupa
 * praticamente 100% da área acima da dobra (100svh de altura, w-full).
 * Um container com scroll-snap mandatório no eixo X "captura" o gesto de
 * wheel/trackpad inteiro (inclusive a parte vertical) sempre que o cursor
 * está sobre ele e ainda há slide pra rolar horizontalmente, em vez de
 * deixar o delta vertical propagar pra rolagem da página — daí o scroll
 * "não descer" só enquanto o mouse estiver dentro do banner (fora dele,
 * nas margens de 48/120px reservadas pras setas, ou mais abaixo na
 * página, a rolagem normal do documento nunca teve esse container no
 * caminho e sempre funcionou).
 *
 * Fix: reencaminhamos manualmente pra rolagem da página qualquer gesto de
 * wheel predominantemente VERTICAL (|deltaY| > |deltaX|), sem mexer no
 * caso horizontal — troca de slide via trackpad/scroll lateral continua
 * funcionando como antes, só a parte vertical deixa de ficar presa.
 * Setas/dots/autoplay (SliderJS) não usam wheel, não são afetados.
 */
function BannerScrollGuard({ rootId }: { rootId: string }) {
  useEffect(() => {
    const slider = document
      .getElementById(rootId)
      ?.querySelector<HTMLElement>("[data-slider]");

    if (!slider) return;

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      event.preventDefault();
      window.scrollBy({ top: event.deltaY, left: 0 });
    };

    slider.addEventListener("wheel", onWheel, { passive: false });

    return () => slider.removeEventListener("wheel", onWheel);
  }, [rootId]);

  return null;
}

/**
 * Mantém `--banner-offset-top` (quanto do topo da página é ocupado por
 * header/barra de avisos antes do banner) em dia: no mount — cobrindo a
 * navegação client-side, onde o <script> inline do markup não roda — e a cada
 * resize, já que a altura do header muda entre breakpoints.
 */
function BannerOffsetTracker({ rootId }: { rootId: string }) {
  useEffect(() => {
    const el = document.getElementById(rootId);

    if (!el) return;

    const update = () => {
      // Zera antes de medir: o próprio offset entra no cálculo da altura, e
      // um valor velho deslocaria a medição seguinte.
      el.style.setProperty("--banner-offset-top", "0px");
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY));
      el.style.setProperty("--banner-offset-top", `${top}px`);
    };

    update();
    window.addEventListener("resize", update);

    return () => window.removeEventListener("resize", update);
  }, [rootId]);

  return null;
}

function BannerCarousel(props: Props) {
  const {
    images,
    preload,
    interval,
    showArrows,
    maxHeight,
    desktopAspectRatio,
    mobileAspectRatio,
  } = {
    ...DEFAULT_PROPS,
    ...props,
  };

  const id = useId();
  const loadedImages = useSignal(new Set([0]));

  const validImages = images?.filter(isValidBanner) || [];

  if (images && images.length !== validImages.length) {
    console.warn(
      "BannerCarousel: Banners inválidos foram removidos:",
      images.filter((b) => !isValidBanner(b)),
    );
  }

  const handleImageLoad = (index: number) => {
    loadedImages.value.add(index);
  };

  if (validImages.length === 0) {
    return (
      <div className="banner-carousel w-full h-64 bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">Nenhum banner válido disponível</p>
      </div>
    );
  }

  return (
    <div
      id={id}
      // `overflow-hidden` é a rede de segurança final contra o banner
      // "sambando"/vazando: qualquer conteúdo que por algum motivo exceda a
      // caixa (ex: vídeo com poster de proporção diferente) é cortado aqui
      // dentro, nunca sobrepõe a seção seguinte da página durante o scroll.
      className="banner-carousel relative w-full max-w-full overflow-hidden grid grid-cols-[48px_1fr_48px] sm:grid-cols-[120px_1fr_120px] grid-rows-[1fr_48px_1fr_48px]"
      style={{
        // A altura vem da regra `.banner-carousel` em styles/app.css, que a
        // calcula pela PROPORÇÃO da peça (mobile/desktop) limitada a uma
        // tela — assim a caixa tem a mesma proporção da mídia e o
        // `object-cover` não precisa aparar nada: sem faixa branca e sem
        // corte na arte, em qualquer device. Aqui só passamos as variáveis
        // que aquela regra consome (e que o editor pode sobrescrever).
        ...(desktopAspectRatio
          ? { ["--banner-aspect-desktop" as string]: `${desktopAspectRatio}` }
          : {}),
        ...(mobileAspectRatio
          ? { ["--banner-aspect-mobile" as string]: `${mobileAspectRatio}` }
          : {}),
        // Só emitimos o teto quando a instância pede um: com `100svh` fixo
        // (o default de antes) a caixa ficava mais BAIXA que a proporção da
        // peça em telas largas e o `object-cover` cortava a arte — topo
        // (rosto da modelo) e rodapé.
        ...(maxHeight ? { ["--banner-max-height" as string]: maxHeight } : {}),
      }}
    >
      {/*
        Mede, já durante o parse do HTML (antes da primeira pintura, sem
        "pulo" de layout), a distância entre o topo da página e o topo do
        banner, e entrega esse valor pra regra de CSS acima descontar do teto
        de altura. O `BannerOffsetTracker` abaixo refaz a conta em resize e
        em navegação client-side, onde este script não é reexecutado.
      */}
      <script
        dangerouslySetInnerHTML={{
          __html:
            "(function(){var e=document.currentScript.parentElement;" +
            "e.style.setProperty('--banner-offset-top'," +
            "Math.max(0,Math.round(e.getBoundingClientRect().top+window.scrollY))+'px')})()",
        }}
      />

      <Slider className="carousel carousel-center w-full max-w-full h-full col-span-full row-span-full gap-6">
        {validImages.map((image, index) => (
          <Slider.Item
            index={index}
            className="carousel-item w-full max-w-full h-full"
            data-index={index}
          >
            <BannerItem
              image={image}
              lcp={index === 0 && preload}
              onLoad={() => handleImageLoad(index)}
            />
          </Slider.Item>
        ))}
      </Slider>

      {showArrows && validImages.length > 1 && <Buttons />}
      <Dots images={validImages} interval={interval} />

      <SliderJS rootId={id} interval={interval && interval * 1e3} infinite />
      <BannerScrollGuard rootId={id} />
      <BannerOffsetTracker rootId={id} />
    </div>
  );
}

export default BannerCarousel;
